import { z, ZodObject } from "zod"

import {
  CertificateCategorySchema,
  CertificateCategoryType,
} from "@tfg/types/schemas/enum"

import { UpdateCertificateRequestDto } from "~/gen/api/types"
import { formFieldType } from "~/pages/Admin/components/TypesForm.tsx"

export const typeObject = z.object({
  name: z.string().nullable(),
})

export const baseTemplate = z.object({
  name: z.string().nonempty(),
  reference: z.string().nonempty(),
  issueDate: z.coerce.date().nullable(),
  expiryDate: z.coerce.date().nullable(),
  typeId: z.string().nullable(),
  score: z.string().nullable(),
  certificationBody: z.string().nullable(),
  certifiedOrganizationName: z.string().nullable(),
  certifiedOrganizationAddress: z.string().nullable(),
  certifiedOrganizationLicenseNumber: z.string().nullable(),
})

export const scopeCertificateTemplate = baseTemplate
  .extend({
    productCategories: z.string().array().nullable(),
    productDetails: z.string().array().nullable(),
    processingSteps: z.string().array().nullable(),
  })
  .partial()

export const transactionCertificateTemplate = baseTemplate
  .extend({
    productCategories: z.string().array().nullable(),
    buyerOfCertifiedProducts: z.string().nullable(),
    certifiedWeight: z.string().nullable(),
    linkedScopeCertificateNumber: z.string().nullable(),
  })
  .partial()

export const otherInitiativeCertificateTemplate = baseTemplate
  .extend({
    processingSteps: z.string().array().nullable(),
    score: z.string().nullable(),
  })
  .partial()

export type CertificateTemplateType = {
  [key in CertificateCategoryType]: z.ZodObject<any>
}

export const certificateTemplates: CertificateTemplateType = {
  [CertificateCategorySchema.enum.SCOPE]: scopeCertificateTemplate,
  [CertificateCategorySchema.enum.TRANSACTION]: transactionCertificateTemplate,
  [CertificateCategorySchema.enum.OTHER_INITIATIVES]:
    otherInitiativeCertificateTemplate,
  [CertificateCategorySchema.enum.UNKNOWN]: scopeCertificateTemplate,
}

export type ScopeCertificate = z.infer<typeof scopeCertificateTemplate>
export type TransactionCertificate = z.infer<
  typeof transactionCertificateTemplate
>
export type OtherInitiativeCertificate = z.infer<
  typeof otherInitiativeCertificateTemplate
>

export type FilteredCertificate =
  | ScopeCertificate
  | TransactionCertificate
  | OtherInitiativeCertificate

export function getTemplateKeysForForm(
  template: ZodObject<any>
): Array<keyof FilteredCertificate> {
  return Object.keys(template.shape) as Array<keyof FilteredCertificate>
}
export function getCertificateTemplate(
  category: CertificateCategoryType
): ZodObject<any> {
  return certificateTemplates[category]
}

export function filterCertificatesWithTemplate(
  certificate: FilteredCertificate,
  template: ZodObject<any>
): FilteredCertificate | null {
  const parseResult = template.safeParse(certificate)
  if (parseResult.success) {
    return parseResult.data
  } else {
    console.error("Error parsing certificate", parseResult.error)
    return null
  }
}

export function getCertificateFormFieldType(type: string): formFieldType {
  switch (type) {
    case "string":
      return "text"
    case "array":
      return "tagsInput"
    default:
      return "text"
  }
}

const keyTypes: Record<
  | keyof TransactionCertificate
  | keyof ScopeCertificate
  | keyof OtherInitiativeCertificate,
  formFieldType
> = {
  score: "select",
  typeId: "select",
  name: "text",
  reference: "reference",
  issueDate: "date",
  expiryDate: "date",
  productDetails: "tagsInput",
  processingSteps: "tagsInput",
  buyerOfCertifiedProducts: "text",
  certifiedWeight: "text",
  certificationBody: "text",
  certifiedOrganizationName: "text",
  certifiedOrganizationAddress: "text",
  certifiedOrganizationLicenseNumber: "text",
  productCategories: "tagsInput",
  linkedScopeCertificateNumber: "text",
} satisfies Record<
  // omittting status and productionSiteId as are not editable in the form
  keyof Omit<UpdateCertificateRequestDto, "status" | "productionSiteId">,
  formFieldType
>

export function getTypeOfKey(key: keyof FilteredCertificate): formFieldType {
  return keyTypes[key]
}
